import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Link as MuiLink,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link as RouterLink } from 'react-router-dom';

import { authApi } from '../../services/authApi';
import { ApiError } from '../../services/api';

const ForgotSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Please enter a valid email address.'),
});

type ForgotFields = z.infer<typeof ForgotSchema>;

/**
 * Forgotten-password request page (#122). Submits an email; the API always
 * responds the same way whether or not an account exists, so we show a generic
 * confirmation and never reveal account existence.
 */
export function ForgotPasswordPage() {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotFields>({
    resolver: zodResolver(ForgotSchema),
    defaultValues: { email: '' },
  });

  async function onSubmit(values: ForgotFields) {
    setSubmitting(true);
    setError(null);
    try {
      await authApi.forgotPassword(values.email);
      setSubmitted(true);
    } catch (err) {
      // The endpoint is rate-limited; surface that, otherwise a generic error.
      if (err instanceof ApiError && err.httpStatus === 429) {
        setError('Too many attempts. Please wait a minute and try again.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Box sx={{ maxWidth: 480, mx: 'auto' }}>
      <Paper variant="outlined" sx={{ p: { xs: 2, md: 4 } }}>
        <Typography variant="h1" component="h1" sx={{ fontSize: '1.6rem', mb: 1 }}>
          Reset your password
        </Typography>

        {submitted ? (
          <Alert severity="success" role="status" sx={{ mb: 2 }}>
            If an account exists for that email, we&apos;ve sent a link to reset your password. The
            link expires in 1 hour.
          </Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Enter your email and we&apos;ll send you a link to choose a new password.
            </Typography>

            {error ? (
              <Alert severity="error" role="alert" sx={{ mb: 2 }}>
                {error}
              </Alert>
            ) : null}

            <Box
              component="form"
              onSubmit={handleSubmit(onSubmit)}
              noValidate
              aria-label="Reset password"
            >
              <Stack spacing={2}>
                <TextField
                  label="Email"
                  type="email"
                  autoComplete="email"
                  required
                  fullWidth
                  {...register('email')}
                  error={Boolean(errors.email)}
                  helperText={errors.email?.message ?? ' '}
                  inputProps={{ 'aria-invalid': Boolean(errors.email) }}
                  InputLabelProps={{ shrink: true }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={submitting}
                  aria-busy={submitting}
                >
                  {submitting ? 'Sending…' : 'Send reset link'}
                </Button>
              </Stack>
            </Box>
          </>
        )}

        <Typography variant="body2" sx={{ mt: 3 }}>
          Remembered it?{' '}
          <MuiLink component={RouterLink} to="/login">
            Back to sign in
          </MuiLink>
          .
        </Typography>
      </Paper>
    </Box>
  );
}

export default ForgotPasswordPage;
